import { applyTimeToMoment } from '@gik/core/utils/moment';
import { dateTimeFormatDetailed } from '@gik/l10n';
import moment from 'moment';
import RRule, { Frequency, Options, Weekday } from 'rrule';
import { timeFromInt, timeToInt } from 'time-number';
import { ICalendarEventFormValues } from './CalendarEventForm';
import { CalendarEventTypeIds } from '@gik/calendar/models/CalendarEventTypes';
import { CreateEventPayload, CreateUpdateEventPayload } from '@gik/api/calendar/calendarEvents';
import { CalendarAnnouncementFormFieldNames, ICalendarAnnouncementFormValues } from '../Announcements';
import { ICalendarEntry } from '@gik/calendar/models/Calendar';
import { ICalendarAnnouncementPayload } from '@gik/api/calendar/calendarAnnouncements';
import { IWordpressCalendarEventType } from '@gik/core/models/wordpress/WordpressCalendarEventType';
import { claimsApi } from '@gik/api/calendar';
import { Analytics } from '@gik/core/utils';
import { UI } from '@gik/ui';
import { userStoreApi } from '@gik/core/store/UserStore';
import { ICalendarClaim } from '@gik/calendar/models/Claim';

export function calendarTimeValuesToPayload(values: ICalendarEventFormValues | ICalendarAnnouncementFormValues) {
  let startDate = moment(values.startDate);
  let endDate = moment(values.endDate);

  const weekdays: Weekday[] = [];

  let interval = 1;

  let rule: RRule;

  if (values.repeat !== -1) {
    let freq: Frequency;
    const weekMap = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU];

    switch (values.repeat) {
      case 10:
        // create weekday strings
        values.weekdays.map((weekday, index) => {
          if (weekday) weekdays.push(weekMap[index]);
        });

        freq = RRule.WEEKLY;

        break;
      case 11: // every other day
        freq = RRule.DAILY;
        interval = 2;

        break;
      default:
        freq = values.repeat || RRule.WEEKLY;
    }

    const ruleOpts: Partial<Options> = {
      byweekday: weekdays,
      freq,
      interval,
    };
    rule = new RRule(ruleOpts);
  } else {
    endDate = startDate;
  }

  // set times on start / end dates
  if (!values.allDay) {
    startDate = applyTimeToMoment(startDate.clone(), `${timeFromInt(values.startTime)}:00.000`);
    // TODO: if before startDate, add an extra day? (event passes midnight)
    endDate = applyTimeToMoment(endDate.clone(), `${timeFromInt(values.endTime)}:00.000`);
  } else {
    startDate = applyTimeToMoment(startDate.clone(), `00:00:00.000`);
    endDate = applyTimeToMoment(endDate.clone(), `00:00:00.000`);
  }

  // if end date is before start Date then ad one day to it
  if (endDate.isBefore(startDate) || endDate.isSame(startDate)) {
    endDate.add(1, 'day');
  }

  return {
    startsAt: startDate.format(dateTimeFormatDetailed),
    endsAt: endDate.format(dateTimeFormatDetailed),
    repeat: rule ? rule.toString() : '',
    sendEmailToPageFollowers: values[CalendarAnnouncementFormFieldNames.SendEmailToPageFollowers],
    allDay: values.allDay,
  };
}

export function calendarAnnouncementFormValuesToPayload(
  values: ICalendarAnnouncementFormValues
): ICalendarAnnouncementPayload {
  const repeatProps = calendarTimeValuesToPayload(values);

  const payload = {
    title: values.title,
    description: values.description,
    announcementTypeId: values.announcementTypeId,
    ...repeatProps,
  };

  // remove null values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null) delete payload[key];
  });

  // delete endsAt if this is an allday event without a repeat
  if (payload.allDay && !repeatProps.repeat) delete payload.endsAt;

  return {
    ...payload,
    typeId: undefined, // to be filled later
  };
}

export function calendarEventFormValuesToPayload(
  values: ICalendarEventFormValues,
  giftCardsOnly: boolean
): CreateEventPayload {
  const repeatProps = calendarTimeValuesToPayload(values);

  const formValues = { ...values, petCareTypeId: parseInt(values.petCareTypeId) };

  const payload: CreateUpdateEventPayload = {
    ...formValues,
    ...repeatProps,
    typeId: values.eventTypeId,
    inkindRouteId: undefined, // to be filled later
  };

  delete payload['eventTypeId'];
  delete payload['startDate'];
  delete payload['startTime'];
  delete payload['endDate'];
  delete payload['endTime'];

  if (!payload.allowGiftCards) {
    delete payload.allowedGiftCardIds;
  }

  if (giftCardsOnly) {
    payload.allDay = true;
  }

  payload.petCareTypeId = parseInt(values.petCareTypeId);

  // delete endsAt if this is an allday event without a repeat
  if (payload.allDay && !repeatProps.repeat) delete payload.endsAt;

  // remove null values
  Object.keys(payload).forEach(key => {
    if (payload[key] === null) delete payload[key];
  });

  return {
    ...payload,
  };
}

export function afterClaimSucceeded(
  entry: ICalendarEntry,
  claim: Partial<ICalendarClaim>,
  eventType: IWordpressCalendarEventType
) {
  const userId = userStoreApi.getState().id;

  const isEditing = !!entry.claim;
  // NOTE: this event name is deliberately lower cased for backwards compatibility
  Analytics.fireEvent('eventClaimed', {
    isUpdate: isEditing.toString(),
    serviceCategorySlug: claim.serviceCategorySlug,
    serviceSlug: claim.serviceSlug,
    calendarEventType: entry.typeName,
    calendarEventTypeCategory: eventType.acf.event_category.value,
    userId,
  });
}

export async function unclaimCalendarEvent(
  entry: ICalendarEntry,
  calendarEventTypes: IWordpressCalendarEventType[],
  userId: string
) {
  const response = await claimsApi.unclaim(entry.claim?.id);
  if (response.ok) {
    const calendarEventType = calendarEventTypes.find(type => type.id === entry.typeId);
    // NOTE: this event name is deliberately lower cased for backwards compatibility
    Analytics.fireEvent('eventUnclaimed', {
      serviceCategorySlug: entry.claim.serviceCategorySlug,
      serviceSlug: entry.claim.serviceSlug,
      calendarEventType: entry.typeName,
      calendarEventTypeCategory: calendarEventType.acf.event_category.value,
      userId: userId,
      // TODO: add claim ID (and change to soft delete of claims)
    });
  } else {
    UI.alert('Please try again.', { title: 'Unclaim failed' });
  }

  return response;
}

export function isFoodType(typeId: number): boolean {
  return (
    typeId === CalendarEventTypeIds.Breakfast ||
    typeId === CalendarEventTypeIds.Dinner ||
    typeId === CalendarEventTypeIds.Lunch
  );
}

export function isPetType(typeId: number): boolean {
  return typeId === CalendarEventTypeIds.PetCare;
}

export function isAnnouncement(typeId: number): boolean {
  return typeId === CalendarEventTypeIds.Announcement;
}

export function getStartTimeByTypeId(typeId: number) {
  // update time if type is breakfast, lunch or dinner
  switch (typeId) {
    case CalendarEventTypeIds.Breakfast:
      return timeToInt('08:00');
    case CalendarEventTypeIds.Lunch:
      return timeToInt('11:00');
    case CalendarEventTypeIds.Dinner:
      return timeToInt('17:00');
    default:
      return timeToInt('12:00');
  }
}
